import { useEffect, useMemo } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { QuickLanguageOption } from '@/components/atoms/QuickLanguageOption/QuickLanguageOption';
import { PopoverCombobox } from '@/components/ui/custom/PopoverCombobox';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { LLMSelector } from '@/modules/aiAgents/components/LLMSelector';
import { languageByCountryCode } from '@/shared/languageCodes';
import { BrainLanguage } from '@/shared/types/brain';
import { formatLanguageLabel, prioritizeLanguageOptions } from '@/util/languageOptions';

import { Form } from './ModalAIAgentCreate';

export const Step2 = ({ prefillName }: { prefillName?: string }) => {
  // Custom hooks
  const { t } = useTranslation();

  // RHF
  const { setValue, control, getValues } = useFormContext<Form>();

  // Local variables
  const languageOptions = useMemo(
    () =>
      prioritizeLanguageOptions(
        Object.keys(languageByCountryCode).map((i) => ({
          value: i.toLowerCase(),
          label: formatLanguageLabel(i),
        }))
      ),
    []
  );
  const brainType = useWatch({ control, name: 'brain_type' });
  const fallbackName = t(`ai_agents.types.${brainType}.title`) + ' ' + t('ai_agents.agent');

  // Initialize form values on mount
  useEffect(() => {
    setValue('provider', 'moveo');
    setValue('language_model_id', 'Default');
    const currentName = getValues('name');
    if (!currentName) {
      setValue('name', prefillName || fallbackName, { shouldValidate: true });
    }
    // Run only on mount to set initial form values; setValue is stable from react-hook-form
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const watchLanguage = useWatch({ control, name: 'language' });

  // Handlers
  const handleChangeLanguage = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    const language = (e.currentTarget as HTMLButtonElement).dataset.language as BrainLanguage;
    setValue('language', language, { shouldValidate: true });
  };

  return (
    <>
      <FormField
        control={control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel>{t('common.name')}</FormLabel>
            <FormControl>
              <Input
                {...field}
                value={field.value ?? fallbackName}
                placeholder={t('common.name_placeholder')}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={control}
        name="language"
        render={({ field, fieldState }) => (
          <FormItem className="mt-4">
            <FormLabel>{t('field.language')}</FormLabel>
            <FormControl>
              <PopoverCombobox
                options={languageOptions}
                value={field.value ?? ''}
                onChange={field.onChange}
                triggerPlaceholder={t('common.select_an_option')}
                error={!!fieldState.error}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="mt-2 flex items-center gap-2" role="group" aria-label={t('field.language')}>
        <QuickLanguageOption
          data-language={BrainLanguage.ENGLISH}
          handleClick={handleChangeLanguage}
          language="en"
          isActive={watchLanguage === 'en'}
        />
        <QuickLanguageOption
          data-language={BrainLanguage.PORTUGUESE_BRAZILIAN}
          handleClick={handleChangeLanguage}
          language="pt-br"
          isActive={watchLanguage === 'pt-br'}
        />
        <QuickLanguageOption
          data-language={BrainLanguage.SPANISH}
          handleClick={handleChangeLanguage}
          language="es"
          isActive={watchLanguage === 'es'}
        />
        <QuickLanguageOption
          data-language={BrainLanguage.GREEK}
          handleClick={handleChangeLanguage}
          language="el"
          isActive={watchLanguage === 'el'}
        />
      </div>

      <LLMSelector className="mt-6 flex gap-4" />
    </>
  );
};
